import React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const tooltipItemSx = { fontSize: 11, lineHeight: 1.35, mb: 0.75 };

const tooltipContent = (
    <Box sx={{ p: 0.75, maxWidth: 340, '& code': { fontSize: 10 } }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: 12, lineHeight: 1.3, display: 'block', mb: 0.75 }}>
            Top-level JSON Layout Keys:
        </Typography>

        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', fontSize: 11, mb: 0.25 }}>
            • agency_logo, foremost_logo, agency_logo2, foremost_logo2
        </Typography>
        <Box sx={{ pl: 1.5, mb: 1 }}>
            <Typography variant="caption" component="div" sx={tooltipItemSx}>
                <code>agency_logo2</code> and <code>foremost_logo2</code> are optional second placements. The same transform options apply to all four logo keys:
            </Typography>
            <Typography variant="caption" component="div" sx={{ ...tooltipItemSx, pl: 1 }}>
                ├─ <strong>width / height:</strong> Percent of template size (bounding box, <code>c_pad</code>).
            </Typography>
            <Typography variant="caption" component="div" sx={{ ...tooltipItemSx, pl: 1 }}>
                ├─ <strong>rotate (angle):</strong> Angle in degrees (e.g. <code>45</code> or <code>-30</code>).
            </Typography>
            <Typography variant="caption" component="div" sx={{ ...tooltipItemSx, pl: 1 }}>
                ├─ <strong>perspective:</strong> 3D tilt, e.g. <code>&#123;&quot;x_angle&quot;: 18, &quot;y_angle&quot;: -22, &quot;z_angle&quot;: 5&#125;</code>.
            </Typography>
            <Typography variant="caption" component="div" sx={{ ...tooltipItemSx, pl: 1 }}>
                ├─ <strong>distort:</strong> 4-corner % <code>[x1, y1, x2, y2, x3, y3, x4, y4]</code> or <code>&quot;x1:y1:...&quot;</code>.
            </Typography>
            <Typography variant="caption" component="div" sx={{ ...tooltipItemSx, pl: 1 }}>
                ├─ <strong>radialize + displace:</strong> Cylinder wrap, e.g. <code>&quot;radialize&quot;: true, &quot;displace&quot;: &#123;&quot;x&quot;: 0, &quot;y&quot;: -8&#125;</code>.
            </Typography>
            <Typography variant="caption" component="div" sx={{ ...tooltipItemSx, pl: 1 }}>
                ├─ <strong>radialize (standalone):</strong> Arc angle (<code>distort:arc</code>), e.g. <code>30</code> or <code>&#123;&quot;angle&quot;: -25&#125;</code>.
            </Typography>
            <Typography variant="caption" component="div" sx={{ ...tooltipItemSx, pl: 1 }}>
                └─ <strong>displace (standalone):</strong> Shear offset (<code>shear</code>), e.g. <code>&#123;&quot;x&quot;: 10, &quot;y&quot;: 20&#125;</code>.
            </Typography>
        </Box>

        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', fontSize: 11, mb: 0.25 }}>
            • line
        </Typography>
        <Box sx={{ pl: 1.5, mb: 1 }}>
            <Typography variant="caption" component="div" sx={tooltipItemSx}>
                Draws a stroke as template size %: <code>[x1, y1, x2, y2]</code> or <code>&quot;x1:y1:x2:y2&quot;</code>, e.g. <code>&quot;line&quot;: [10, 10, 20, 20]</code>. Stroke defaults to 4px (override with <code>border_width</code>). Color follows foremost logo color or <code>logo_color</code>.
            </Typography>
        </Box>

        <Typography variant="caption" component="div" sx={{ fontWeight: 'bold', fontSize: 11, mb: 0.25 }}>
            • font_family, font_size, logo_color, border_width
        </Typography>
        <Box sx={{ pl: 1.5 }}>
            <Typography variant="caption" component="div" sx={{ ...tooltipItemSx, mb: 0 }}>
                Global overrides for default logo preview text styling, line/border stroke width, and colors.
            </Typography>
        </Box>
    </Box>
);

const ViewDataHelpText = () => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <Typography color="text.secondary" variant="caption">
            JSON layout &amp; transformation options (perspective, rotate, distort, radialize, displace, line).
        </Typography>
        <Tooltip arrow placement="top" title={tooltipContent}>
            <HelpOutlineIcon sx={{ fontSize: 16, cursor: 'pointer', color: 'primary.main' }} />
        </Tooltip>
    </Box>
);

export default ViewDataHelpText;
